import { useState, useEffect } from 'react';
import { useOrder } from '../../context/OrderContext';
import { needsTwoFinishColors, shouldUseIdenticalFinishes, normalizeProfileType } from '../../utils/formatters';
import { RALModal } from '../../modals';

const FINISHES = [
  { code: 'A', name: 'Argento Ossidato 20 micron', color: '#C0C0C0' },
  { code: 'B', name: 'Bianco - RAL 9010 Opaco', color: '#FFFFFF' },
  { code: 'N', name: 'Nero - RAL 9005 Opaco', color: '#000000' },
  { code: 'G', name: 'Grezzo', color: '#E0E0E0' },
  { code: 'V2', name: 'Verniciatura Speciale - da 18 a 120 metri escuso', color: '#D3D3D3' },
  { code: 'V1', name: 'Verniciatura Speciale - superiore a 120 metri incluso', color: '#D3D3D3' },
];

// Finiture per profilo N176/N186 (Argento = Simiargento NK1)
const FINISHES_PROFILE_176_186 = [
  { code: 'A', name: 'Semiargento - NK1', color: '#C0C0C0' },
  { code: 'B', name: 'Bianco - RAL 9010 Opaco', color: '#FFFFFF' },
  { code: 'N', name: 'Nero - RAL 9005 Opaco', color: '#000000' },
  { code: 'G', name: 'Grezzo', color: '#E0E0E0' },
  { code: 'V2', name: 'Verniciatura Speciale - da 18 a 120 metri escuso', color: '#D3D3D3' },
  { code: 'V1', name: 'Verniciatura Speciale - superiore a 120 metri incluso', color: '#D3D3D3' },
];

// Finiture per profilo N106 (include RAL 7016 Opaco)
const FINISHES_PROFILE_106 = [
  { code: 'A', name: 'Argento Ossidato 20 micron', color: '#C0C0C0' },
  { code: 'B', name: 'Bianco - RAL 9010 Opaco', color: '#FFFFFF' },
  { code: 'N', name: 'Nero - RAL 9005 Opaco', color: '#000000' },
  { code: 'C', name: 'Grigio Antracite - RAL 7016 Opaco', color: '#293133' },
  { code: 'G', name: 'Grezzo', color: '#E0E0E0' },
  { code: 'V2', name: 'Verniciatura Speciale - da 18 a 120 metri escuso', color: '#D3D3D3' },
  { code: 'V1', name: 'Verniciatura Speciale - superiore a 120 metri incluso', color: '#D3D3D3' },
];

// Helper to check if finish requires RAL code
const isSpecialFinish = (code) => code === 'V1' || code === 'V2';

// Helper to get finishes array based on profile type
const getFinishesForProfile = (profileType) => {
  if (profileType === '106') return FINISHES_PROFILE_106;
  if (profileType === '176' || profileType === '186') return FINISHES_PROFILE_176_186;
  return FINISHES;
};

const FinishSelector = ({ selectedFinish, onSelect }) => {
  const { state } = useOrder();
  const { profileType: rawProfileType, carterCode, hasCarter } = state.orderDetails;
  const profileType = normalizeProfileType(rawProfileType);

  const [finishY, setFinishY] = useState(selectedFinish?.finishY || '');
  const [finishC, setFinishC] = useState(selectedFinish?.finishC || '');
  const [needsSecondColor, setNeedsSecondColor] = useState(false);

  // RAL Modal state
  const [showRALModal, setShowRALModal] = useState(false);
  const [pendingFinish, setPendingFinish] = useState(null);
  const [pendingFinishTarget, setPendingFinishTarget] = useState(null); // 'Y' or 'C' or 'both' or 'finishCOnly'

  // Update local state when selectedFinish prop changes
  useEffect(() => {
    if (selectedFinish) {
      setFinishY(selectedFinish.finishY || '');
      setFinishC(selectedFinish.finishC || '');
    }
  }, [selectedFinish]);

  // Reset finish selections when profile type or carter code changes
  useEffect(() => {
    setFinishY('');
    setFinishC('');
    setNeedsSecondColor(false);
  }, [profileType, carterCode]);

  useEffect(() => {
    // Check if we need two different colors
    const needsTwoColors = needsTwoFinishColors(profileType, carterCode);
    setNeedsSecondColor(needsTwoColors && finishY === 'G');

    // If finishes should be identical, sync them
    if (shouldUseIdenticalFinishes(profileType) && finishY && finishY !== finishC) {
      setFinishC(finishY);
    }
  }, [profileType, carterCode, finishY, finishC]);

  // Handle RAL confirmation
  const handleRALConfirm = (ralCode) => {
    setShowRALModal(false);

    if (pendingFinishTarget === 'finishCOnly') {
      // Only finishC (for profiles without carter or N50)
      onSelect({ finishC: pendingFinish, ralCode });
    } else if (pendingFinishTarget === 'both') {
      // Both finishes are the same
      setFinishY(pendingFinish);
      setFinishC(pendingFinish);
      onSelect({ finishY: pendingFinish, finishC: pendingFinish, ralCode });
    } else if (pendingFinishTarget === 'Y') {
      // Only finishY (carter)
      setFinishY(pendingFinish);
      onSelect({ finishY: pendingFinish, finishC: finishC || 'G', ralCode });
    } else if (pendingFinishTarget === 'C') {
      // Only finishC (profile)
      setFinishC(pendingFinish);
      onSelect({ finishY, finishC: pendingFinish, ralCode });
    } else if (pendingFinishTarget === 'Y_with_G') {
      // Y with finishC fixed to G
      setFinishY(pendingFinish);
      setFinishC('G');
      onSelect({ finishY: pendingFinish, finishC: 'G', ralCode });
    }

    setPendingFinish(null);
    setPendingFinishTarget(null);
  };

  // Handle RAL modal close
  const handleRALClose = () => {
    setShowRALModal(false);
    setPendingFinish(null);
    setPendingFinishTarget(null);
  };

  // Helper to handle finish selection with RAL check
  const handleFinishWithRAL = (code, target, extraData = {}) => {
    if (isSpecialFinish(code)) {
      setPendingFinish(code);
      setPendingFinishTarget(target);
      setShowRALModal(true);
    } else {
      // No RAL needed, proceed normally
      if (target === 'finishCOnly') {
        onSelect({ finishC: code, ...extraData });
      } else if (target === 'both') {
        setFinishY(code);
        setFinishC(code);
        onSelect({ finishY: code, finishC: code, ...extraData });
      } else if (target === 'Y') {
        setFinishY(code);
        onSelect({ finishY: code, finishC: finishC || 'G', ...extraData });
      } else if (target === 'C') {
        setFinishC(code);
        onSelect({ finishY, finishC: code, ...extraData });
      } else if (target === 'Y_with_G') {
        setFinishY(code);
        setFinishC('G');
        onSelect({ finishY: code, finishC: 'G', ...extraData });
      }
    }
  };

  const handleFinishYSelect = (code) => {
    // If finishes should be identical, update both
    if (shouldUseIdenticalFinishes(profileType)) {
      handleFinishWithRAL(code, 'both');
    } else {
      handleFinishWithRAL(code, 'Y');
    }

    // Check if we need a second color selection
    if (needsTwoFinishColors(profileType, carterCode) && code === 'G') {
      setNeedsSecondColor(true);
    } else {
      setNeedsSecondColor(false);
    }
  };

  const handleFinishCSelect = (code) => {
    handleFinishWithRAL(code, 'C');
  };

  // Render finish button
  const renderFinishButton = (finish, isSelected, onClick) => (
    <button
      key={finish.code}
      onClick={onClick}
      className={`p-4 border rounded-lg ${isSelected ? 'bg-red-100 border-red-500' : 'border-gray-300'}`}
    >
      <div className="flex flex-col items-center">
        <div
          className="w-16 h-16 rounded-full mb-2 border border-gray-300"
          style={{ backgroundColor: finish.color }}
        ></div>
        <span className="font-medium">{finish.name}</span>
        <span className="text-sm text-gray-500">({finish.code})</span>
      </div>
    </button>
  );

  // For N50, we only need to select finishC
  if (profileType === '50') {
    return (
      <div className="text-center">
        <h2 className="text-xl font-bold mb-4">Seleziona la Finitura</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          {FINISHES.map((finish) =>
            renderFinishButton(
              finish,
              selectedFinish?.finishC === finish.code,
              () => handleFinishWithRAL(finish.code, 'finishCOnly')
            )
          )}
        </div>
        <RALModal
          isOpen={showRALModal}
          onClose={handleRALClose}
          onConfirm={handleRALConfirm}
          finishType={pendingFinish}
        />
      </div>
    );
  }

  // For profiles that don't have carter, we only need finishC
  // ECCEZIONE: N176 e N186 senza carter gestiti separatamente sotto (usano FINISHES_PROFILE_176_186)
  if (!hasCarter && profileType !== '176' && profileType !== '186') {
    const finishesArray = getFinishesForProfile(profileType);
    return (
      <div className="text-center">
        <h2 className="text-xl font-bold mb-4">Seleziona la Finitura</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          {finishesArray.map((finish) =>
            renderFinishButton(
              finish,
              selectedFinish?.finishC === finish.code,
              () => handleFinishWithRAL(finish.code, 'finishCOnly')
            )
          )}
        </div>
        <RALModal
          isOpen={showRALModal}
          onClose={handleRALClose}
          onConfirm={handleRALConfirm}
          finishType={pendingFinish}
        />
      </div>
    );
  }

  // N186 senza carter: l'utente seleziona la finitura del profilo (C)
  // Usa FINISHES_PROFILE_176_186 (Argento = Simiargento NK1)
  if (profileType === '186' && !hasCarter) {
    return (
      <div className="text-center">
        <h2 className="text-xl font-bold mb-4">Seleziona la Finitura del Profilo</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          {FINISHES_PROFILE_176_186.map((finish) =>
            renderFinishButton(
              finish,
              selectedFinish?.finishC === finish.code,
              () => handleFinishWithRAL(finish.code, 'finishCOnly')
            )
          )}
        </div>
        <RALModal
          isOpen={showRALModal}
          onClose={handleRALClose}
          onConfirm={handleRALConfirm}
          finishType={pendingFinish}
        />
      </div>
    );
  }

  // For 176 WITH carter: finishC is ALWAYS G, user selects Y (carter)
  // For 176 WITHOUT carter: user selects finishC (profile)
  if (profileType === '176') {
    // N176 senza carter: l'utente seleziona la finitura del profilo (C)
    // Usa FINISHES_PROFILE_176_186 (Argento = Simiargento NK1)
    if (!hasCarter) {
      return (
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">Seleziona la Finitura del Profilo</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            {FINISHES_PROFILE_176_186.map((finish) =>
              renderFinishButton(
                finish,
                selectedFinish?.finishC === finish.code,
                () => handleFinishWithRAL(finish.code, 'finishCOnly')
              )
            )}
          </div>
          <RALModal
            isOpen={showRALModal}
            onClose={handleRALClose}
            onConfirm={handleRALConfirm}
            finishType={pendingFinish}
          />
        </div>
      );
    }

    // N176 con carter: C = G (sempre), Y = colore carter
    // Usa FINISHES_PROFILE_176_186 (Argento = Simiargento NK1)
    return (
      <div className="text-center">
        <h2 className="text-xl font-bold mb-4">Seleziona la Finitura del Carter</h2>
        <p className="mb-4 text-gray-600">Per NINFA 176, la finitura del profilo è sempre G (Grezzo)</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          {FINISHES_PROFILE_176_186.map((finish) =>
            renderFinishButton(
              finish,
              selectedFinish?.finishY === finish.code,
              () => handleFinishWithRAL(finish.code, 'Y_with_G')
            )
          )}
        </div>
        <RALModal
          isOpen={showRALModal}
          onClose={handleRALClose}
          onConfirm={handleRALConfirm}
          finishType={pendingFinish}
        />
      </div>
    );
  }

  // For 186 LINEARE
  // IMPORTANTE: Y = CARTER, C = PROFILO
  if (profileType === '186' && !state.orderDetails.isScale) {
    // For carter 1 or 1I: Profile is ALWAYS Grezzo (G), user only selects carter finish
    // Usa FINISHES_PROFILE_176_186 (Argento = Simiargento NK1)
    if (['1', '1I'].includes(carterCode)) {
      return (
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">Seleziona la Finitura del Carter</h2>
          <p className="mb-4 text-gray-600">Il profilo è sempre Grezzo. Scegli il colore del carter.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            {FINISHES_PROFILE_176_186.map((finish) =>
              renderFinishButton(
                finish,
                finishY === finish.code,
                () => handleFinishWithRAL(finish.code, 'Y_with_G')
              )
            )}
          </div>
          <RALModal
            isOpen={showRALModal}
            onClose={handleRALClose}
            onConfirm={handleRALConfirm}
            finishType={pendingFinish}
          />
        </div>
      );
    }

    // For carter 2 (doppio carter): Two-step process - Profile finish first, then carter finish
    // Step 2: If profile is Grezzo, show carter finish selection
    // Doppio carter + Grezzo: usa FINISHES (Argento Ossidato 20 micron) sul carter
    if (finishC === 'G' && !finishY) {
      return (
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">Seleziona la Finitura del Carter</h2>
          <p className="mb-4 text-gray-600">Il profilo è Grezzo. Scegli il colore del carter.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            {FINISHES.map((finish) =>
              renderFinishButton(
                finish,
                finishY === finish.code,
                () => handleFinishWithRAL(finish.code, 'Y')
              )
            )}
          </div>
          <RALModal
            isOpen={showRALModal}
            onClose={handleRALClose}
            onConfirm={handleRALConfirm}
            finishType={pendingFinish}
          />
        </div>
      );
    }

    // Step 1: Show profile finish selection (for carter 2 only)
    // Usa FINISHES_PROFILE_176_186 (Argento = Simiargento NK1)
    if (!finishC) {
      return (
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">Seleziona la Finitura del Profilo</h2>
          <p className="mb-4 text-gray-600">Scegli la finitura per il profilo NINFA 186</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            {FINISHES_PROFILE_176_186.map((finish) => (
              <button
                key={finish.code}
                onClick={() => {
                  if (isSpecialFinish(finish.code)) {
                    setPendingFinish(finish.code);
                    setPendingFinishTarget(finish.code !== 'G' ? 'both' : 'C');
                    setShowRALModal(true);
                  } else {
                    setFinishC(finish.code);
                    if (finish.code !== 'G') {
                      setFinishY(finish.code);
                      onSelect({ finishY: finish.code, finishC: finish.code });
                    }
                  }
                }}
                className={`p-4 border rounded-lg ${finishC === finish.code ? 'bg-red-100 border-red-500' : 'border-gray-300'}`}
              >
                <div className="flex flex-col items-center">
                  <div
                    className="w-16 h-16 rounded-full mb-2 border border-gray-300"
                    style={{ backgroundColor: finish.color }}
                  ></div>
                  <span className="font-medium">{finish.name}</span>
                  <span className="text-sm text-gray-500">({finish.code})</span>
                </div>
              </button>
            ))}
          </div>
          <RALModal
            isOpen={showRALModal}
            onClose={handleRALClose}
            onConfirm={handleRALConfirm}
            finishType={pendingFinish}
          />
        </div>
      );
    }
  }

  // For 186 IN SCALA, Y and C are the same color
  // PER 186 IN SCALA: Y E C = COLORE (stesso)
  // Usa FINISHES_PROFILE_176_186 (Argento = Simiargento NK1)
  if (profileType === '186' && state.orderDetails.isScale) {
    return (
      <div className="text-center">
        <h2 className="text-xl font-bold mb-4">Seleziona la Finitura</h2>
        <p className="mb-4 text-gray-600">Per NINFA 186 in scala, carter e profilo hanno la stessa finitura</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          {FINISHES_PROFILE_176_186.map((finish) =>
            renderFinishButton(
              finish,
              selectedFinish?.finishY === finish.code,
              () => handleFinishWithRAL(finish.code, 'both')
            )
          )}
        </div>
        <RALModal
          isOpen={showRALModal}
          onClose={handleRALClose}
          onConfirm={handleRALConfirm}
          finishType={pendingFinish}
        />
      </div>
    );
  }

  // For N116 - Two-step process based on carter code
  // IMPORTANTE: Y = CARTER, C = PROFILO
  if (profileType === '116' && hasCarter) {
    // Special case for carter 1C: Only ask for carter finish, profile will be the same
    if (carterCode === '1C') {
      return (
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">Seleziona la Finitura del Carter</h2>
          <p className="mb-4 text-gray-600">Il profilo avrà la stessa finitura del carter.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            {FINISHES.map((finish) =>
              renderFinishButton(
                finish,
                finishY === finish.code,
                () => handleFinishWithRAL(finish.code, 'both')
              )
            )}
          </div>
          <RALModal
            isOpen={showRALModal}
            onClose={handleRALClose}
            onConfirm={handleRALConfirm}
            finishType={pendingFinish}
          />
        </div>
      );
    }

    // Step 2: If profile is Grezzo and carter is 1 or 1S, show carter finish selection
    if (finishC === 'G' && !finishY && ['1', '1S'].includes(carterCode)) {
      return (
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">Seleziona la Finitura del Carter</h2>
          <p className="mb-4 text-gray-600">Il profilo è Grezzo. Scegli il colore del carter.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            {FINISHES.map((finish) =>
              renderFinishButton(
                finish,
                finishY === finish.code,
                () => handleFinishWithRAL(finish.code, 'Y')
              )
            )}
          </div>
          <RALModal
            isOpen={showRALModal}
            onClose={handleRALClose}
            onConfirm={handleRALConfirm}
            finishType={pendingFinish}
          />
        </div>
      );
    }

    // Step 1: Show profile finish selection (if not already selected)
    if (!finishC) {
      return (
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">Seleziona la Finitura del Profilo</h2>
          <p className="mb-4 text-gray-600">Scegli la finitura per il profilo NINFA 116</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            {FINISHES.map((finish) => (
              <button
                key={finish.code}
                onClick={() => {
                  if (isSpecialFinish(finish.code)) {
                    setPendingFinish(finish.code);
                    if (carterCode === '2S' || finish.code !== 'G') {
                      setPendingFinishTarget('both');
                    } else {
                      setPendingFinishTarget('C');
                    }
                    setShowRALModal(true);
                  } else {
                    setFinishC(finish.code);
                    if (carterCode === '2S') {
                      setFinishY(finish.code);
                      onSelect({ finishY: finish.code, finishC: finish.code });
                    } else if (['1', '1S'].includes(carterCode)) {
                      if (finish.code !== 'G') {
                        setFinishY(finish.code);
                        onSelect({ finishY: finish.code, finishC: finish.code });
                      }
                    }
                  }
                }}
                className={`p-4 border rounded-lg ${finishC === finish.code ? 'bg-red-100 border-red-500' : 'border-gray-300'}`}
              >
                <div className="flex flex-col items-center">
                  <div
                    className="w-16 h-16 rounded-full mb-2 border border-gray-300"
                    style={{ backgroundColor: finish.color }}
                  ></div>
                  <span className="font-medium">{finish.name}</span>
                  <span className="text-sm text-gray-500">({finish.code})</span>
                </div>
              </button>
            ))}
          </div>
          <RALModal
            isOpen={showRALModal}
            onClose={handleRALClose}
            onConfirm={handleRALConfirm}
            finishType={pendingFinish}
          />
        </div>
      );
    }
  }

  // Get the appropriate finishes array for the current profile
  const currentFinishes = getFinishesForProfile(profileType);

  return (
    <div className="text-center">
      <h2 className="text-xl font-bold mb-4">
        {needsTwoFinishColors(profileType, carterCode)
          ? 'Seleziona la Finitura del Carter'
          : 'Seleziona la Finitura'}
      </h2>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        {currentFinishes.map((finish) =>
          renderFinishButton(
            finish,
            finishY === finish.code,
            () => handleFinishYSelect(finish.code)
          )
        )}
      </div>

      {needsSecondColor && (
        <>
          <h2 className="text-xl font-bold my-4">Seleziona la Finitura del Profilo</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {currentFinishes.map((finish) =>
              renderFinishButton(
                finish,
                finishC === finish.code,
                () => handleFinishCSelect(finish.code)
              )
            )}
          </div>
        </>
      )}

      <RALModal
        isOpen={showRALModal}
        onClose={handleRALClose}
        onConfirm={handleRALConfirm}
        finishType={pendingFinish}
      />
    </div>
  );
};

export default FinishSelector;
